import { Box, Link } from "@mui/material";
import { useTranslation } from "next-i18next";
import { Typography } from "src/components-mui/base/typography/typography";
import { NAV_SEARCH_RESULTS_TEST_ID } from "src/components-mui/common/typeahead/constants";
import { useExtendedSearchUrl } from "src/components-mui/common/typeahead/hooks/use-extended-search-url";
import { MarketIndicatorSearchItem } from "src/components-mui/common/typeahead/nav-search-item/indices-search-item";
import { UnderlyingSearchItem } from "src/components-mui/common/typeahead/nav-search-item/underlying-search-item";
import { WikifolioSearchItem } from "src/components-mui/common/typeahead/nav-search-item/wikifolio-search-item";
import { SEARCH_RESULTS_LISTBOX_ID } from "src/components-mui/common/typeahead/typeahead.constants";
import { ISearchResults } from "src/components-mui/common/typeahead/typeahead.types";
import { getGlobals } from "src/constants/globals";

const NAV_SEARCH_NO_WIKIFOLIOS_FOUND_MESSAGE_TEST_ID = "nav-search-no-wikifolios-found-message";
const NAV_SEARCH_NO_STOCKS_FOUND_MESSAGE_TEST_ID = "nav-search-no-stocks-found-message";
const NAV_SEARCH_NO_MARKET_INDICATORS_FOUND_MESSAGE_TEST_ID = "nav-search-no-market-indicators-found-message";

const DESKTOP_RESULTS_MAX_HEIGHT = "calc(80vh - 40px)";

export const GlobalSearchResults = ({ results, selectedIndex, query, placement = "top" }: ISearchResults) => {
    const globals = getGlobals();
    const { getExtendedSearchUrl } = useExtendedSearchUrl();
    const { t } = useTranslation("common");

    const { wikifolios, underlyings, marketIndicators } = results ?? {};

    const wikifoliosLength = wikifolios?.length ?? 0;
    const underlyingsLength = underlyings?.length ?? 0;

    const noWfFound = wikifoliosLength === 0;
    const noUnderLyingsFound = underlyingsLength === 0;
    const noMarketIndicatorsFound = marketIndicators?.length === 0;

    const renderSeeAllMatchingWFUrl = !noWfFound && query;

    return (
        <Box
            id={SEARCH_RESULTS_LISTBOX_ID}
            role="listbox"
            aria-label={t("search-results")}
            sx={{
                border: ["none", "none", "1px solid", "1px solid"],
                borderRadius: "8px",
                bgcolor: "white",
                position: ["relative", "relative", "absolute", "absolute"],
                width: "100%",
                maxHeight: ["none", "none", DESKTOP_RESULTS_MAX_HEIGHT, DESKTOP_RESULTS_MAX_HEIGHT],
                overflow: ["visible", "visible", "hidden auto", "hidden auto"],
                borderColor: "gray.400",
                mt: [1, 1, 2],
                pt: 2,
                pb: 2,
                zIndex: ["initial", "initial", "modal"],
            }}
            data-test-id={NAV_SEARCH_RESULTS_TEST_ID}
        >
            <Typography
                sx={{
                    fontSize: "lg",
                    fontWeight: "bold",
                    px: [0, 3],
                    pb: 1,
                }}
            >
                {t("wikifolios")}
            </Typography>

            {noWfFound ? (
                <Typography sx={{ px: [0, 3], pb: 1 }} data-test-id={NAV_SEARCH_NO_WIKIFOLIOS_FOUND_MESSAGE_TEST_ID}>
                    {t("wikifolios-not-found")}
                </Typography>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                    {wikifolios?.map((wikifolio, index) => (
                        <WikifolioSearchItem
                            key={wikifolio.resourceIdentifier}
                            query={query}
                            isSelected={index === selectedIndex}
                            resourceIdentifier={wikifolio.resourceIdentifier}
                            shortDescription={wikifolio.shortDescription}
                            performance={wikifolio.performance}
                            trader={wikifolio.trader}
                            securityType={wikifolio.securityType}
                            placement={placement}
                        />
                    ))}
                </Box>
            )}

            {renderSeeAllMatchingWFUrl ? (
                <Link underline="always" sx={{ mt: 2, mx: [0, 3], mb: 3, display: "block" }} href={getExtendedSearchUrl(query)}>
                    {t("see-all-matching-wf")}
                </Link>
            ) : (
                <Link underline="always" sx={{ mt: 2, mx: [0, 3], mb: 3, display: "block" }} href={globals.routes.searchExtended || "/"}>
                    {t("see-all-wikifolios")}
                </Link>
            )}

            <Typography
                sx={{
                    mt: 4,
                    fontSize: "lg",
                    fontWeight: "bold",
                    px: [0, 3],
                    pb: 1,
                }}
            >
                {t("stocks-and-etfs")}
            </Typography>

            {noUnderLyingsFound ? (
                <Typography sx={{ px: [0, 3] }} data-test-id={NAV_SEARCH_NO_STOCKS_FOUND_MESSAGE_TEST_ID}>
                    {t("stocks-etfs-not-found")}
                </Typography>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                    {underlyings?.map((underlying, index) => (
                        <UnderlyingSearchItem
                            key={underlying.resourceIdentifier}
                            query={query}
                            isSelected={index + wikifoliosLength === selectedIndex}
                            resourceIdentifier={underlying.resourceIdentifier}
                            shortDescription={underlying.shortDescription}
                            securityType={underlying.securityType}
                            logoUrl={underlying.logoUrl}
                            placement={placement}
                        />
                    ))}
                </Box>
            )}

            <Typography
                sx={{
                    mt: 4,
                    fontSize: "lg",
                    fontWeight: "bold",
                    px: [0, 3],
                    pb: 1,
                }}
            >
                {t("market-indices")}
            </Typography>

            {noMarketIndicatorsFound ? (
                <Typography sx={{ px: [0, 3], pb: 1 }} data-test-id={NAV_SEARCH_NO_MARKET_INDICATORS_FOUND_MESSAGE_TEST_ID}>
                    {t("market-indices-not-found")}
                </Typography>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                    {marketIndicators?.map((marketIndex, index) => (
                        <MarketIndicatorSearchItem
                            key={marketIndex.resourceIdentifier}
                            placement={placement}
                            query={query}
                            isSelected={index + wikifoliosLength + underlyingsLength === selectedIndex}
                            resourceIdentifier={marketIndex.resourceIdentifier}
                            shortDescription={marketIndex.shortDescription}
                            logoUrl={marketIndex.logoUrl}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};
